import {Component, DestroyRef, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {MapToolsService} from '../../services/map-tools.service';
import {SpatialSnapshotService} from '../../snapshot/spatial-snapshot.service';
import {NgEventBus} from 'ng-event-bus';
import {PermissionsService} from '../../../common/service/permissions.service';
import {switchMap} from 'rxjs/operators';
import {ConfirmationModalService} from '../../../common/component/modal';
import {catchError, finalize, Observable, of, tap} from 'rxjs';
import {SpatialUtilService} from '../../services/spatial-util.service';
import {PlanModelService} from '../../../common/service/plan-model.service';
import {NLR_TABS} from '../../../common/constants';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({
    selector: 'nlr-spatial-snapshots',
    imports: [CommonModule, FormsModule, NgSelectModule],
    providers: [CommonModule],
    templateUrl: './spatial-snapshots.component.html',
    styleUrls: ['./spatial-snapshots.component.scss']
})
export class SpatialSnapshotsComponent implements OnInit, OnDestroy {
    @Input()
    disabled: boolean;

    title: string = 'SpatialSnapshotsComponent';

    editSnapshotsPermissions = ['EDIT_DESIGN'];

    viewSnapshotsPermissions = ['EDIT_DESIGN', 'VIEW_DESIGN'];

    restoreSnapshotTimeout: number;

    snapshotIds = [];

    selectedSnapshotId = '';

    constructor(
        private confirmationModalService: ConfirmationModalService,
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService,
        private mapToolsService: MapToolsService,
        private spatialSnapshotService: SpatialSnapshotService,
        private spatialUtilService: SpatialUtilService
    ) {}

    ngOnInit() {
        this.eventBus
            .on(`nlr:tabChange:${NLR_TABS.DESIGN}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.spatialSnapshotService.planGuid !== this.planModelService.getGuid()) {
                    this.initSpatialSnapshots();
                }
            });
    }

    ngOnDestroy() {
        console.debug(`${this.title} - ngOnDestroy`);
        this.spatialSnapshotService.clearAllSnapshots().subscribe(() => (this.spatialSnapshotService.planGuid = null));
    }

    private initSpatialSnapshots() {
        console.debug(`${this.title} - initSpatialSnapshots`);

        this.spatialSnapshotService
            .init(
                () => this.planModelService.getGuid(),
                () => this.planModelService.getSpatialReference(),
                () => [
                    {featureLayer: this.spatialUtilService.getFeatureLayer('angle'), mapLayer: this.spatialUtilService.getAngleMapLayer()},
                    {featureLayer: this.spatialUtilService.getFeatureLayer('annotation'), mapLayer: this.spatialUtilService.getAnnotationMapLayer()},
                    {
                        featureLayer: this.spatialUtilService.getFeatureLayer('enlargement'),
                        mapLayer: this.spatialUtilService.getEnlargementMapLayer()
                    },
                    {featureLayer: this.spatialUtilService.getControlPointMapLayer()},
                    {featureLayer: this.spatialUtilService.getPointMapLayer()},
                    {featureLayer: this.spatialUtilService.getLineMapLayer()},
                    {featureLayer: this.spatialUtilService.getPolygonMapLayer()},
                    {featureLayer: this.spatialUtilService.getAffectedLotMapLayer()},
                    {featureLayer: this.spatialUtilService.getLotMapLayer()},
                    {featureLayer: this.spatialUtilService.getRoadMapLayer()},
                    {featureLayer: this.spatialUtilService.getConstructPointMapLayer()},
                    {featureLayer: this.spatialUtilService.getConstructLineMapLayer()},
                    {featureLayer: this.spatialUtilService.getInterestMapLayer()},
                    {featureLayer: this.spatialUtilService.getClippingMapLayer()},
                    {featureLayer: this.spatialUtilService.getPolylineMapLayer()},
                    {featureLayer: this.spatialUtilService.getPrintAreaMapLayer()}
                ]
            )
            .pipe(
                switchMap(() => this.spatialSnapshotService.setupSnapshot()),
                tap(() =>
                    this.mapToolsService.init(
                        'SNAPSHOT',
                        () => {
                            const subscription = this.spatialSnapshotService
                                .createSnapshot({showAlert: true})
                                .subscribe(id => this.eventBus.cast('spatialSnapshotCreated', id));
                            this.eventBus.cast('nlr:busy', subscription);
                        },
                        null,
                        () => this.isEditSnapshotsDisabled()
                    )
                )
            )
            .subscribe();
    }

    hasEditSnapshotsPermissions() {
        return this.permissionsService.hasPermission(this.editSnapshotsPermissions);
    }

    isEditSnapshotsDisabled() {
        return this.disabled || !this.permissionsService.hasEditPermission(this.editSnapshotsPermissions);
    }

    hasViewSnapshotsPermissions() {
        return this.permissionsService.hasPermission(this.viewSnapshotsPermissions);
    }

    isViewSnapshotsDisabled() {
        return this.disabled || !this.permissionsService.hasEditPermission(this.viewSnapshotsPermissions);
    }

    getSnapshotTooltip() {
        return this.mapToolsService.getTooltipWithShortcut('SNAPSHOT', 'Create a snap shot or restore point');
    }

    createSnapshot() {
        console.debug(`${this.title} - createSnapshot`);

        this.mapToolsService.activate('SNAPSHOT').subscribe();
    }

    clearAllSnapshots(selectCtrl) {
        selectCtrl.close();
        console.debug(`${this.title} - clearAllSnapshots`);

        this.spatialSnapshotService.clearAllSnapshots().subscribe(id => this.eventBus.cast('clearedAllSnapshots', id));
    }

    restoreSnapshot(id: string) {
        if (this.isEditSnapshotsDisabled()) {
            return;
        }
        console.debug(`${this.title} - restoreSnapshot`);

        const subscription = new Observable<void>(subscriber => {
            this.mapToolsService
                .activate('RESTORE_SNAPSHOT', () => {
                    if (this.restoreSnapshotTimeout) {
                        subscriber.next();
                        subscriber.complete();
                        return;
                    }

                    this.restoreSnapshotTimeout = window.setTimeout(() => {
                        //Prompt user to save another snapshot if current features differ from the base snapshot
                        return this.spatialSnapshotService
                            .featuresMatchBaseSnapshot()
                            .pipe(
                                switchMap(match => {
                                    let observable: Observable<any>;

                                    if (match) {
                                        observable = of(null);
                                    }
                                    if (!match) {
                                        observable = this.confirmationModalService
                                            .showModal({
                                                title: 'Warning',
                                                confirmationText:
                                                    'Would you like to save a snapshot before replacing all current features with features from the' +
                                                    ' selected snapshot?'
                                            })
                                            .pipe(
                                                switchMap(action => {
                                                    if (action === 'yes') {
                                                        return this.spatialSnapshotService.createSnapshot({showAlert: false});
                                                    }
                                                    return of(null);
                                                }),
                                                tap(id => this.eventBus.cast('spatialSnapshotCreated', id))
                                            );
                                    }

                                    //Restore snapshot regardless of whether user chooses to save a new snapshot prior to restore.
                                    return observable.pipe(
                                        switchMap(() => this.spatialSnapshotService.restoreSnapshot(id)),
                                        tap(() => {
                                            this.eventBus.cast('spatialSnapshotRestored', id);
                                            this.mapToolsService.deactivate('RESTORE_SNAPSHOT');
                                        })
                                    );
                                }),
                                catchError(error => {
                                    console.error(`${this.title} - restoreSnapshot - error:`, error);
                                    this.mapToolsService.deactivate('RESTORE_SNAPSHOT');
                                    return of(null);
                                }),
                                // Always release the busy spinner and reset the timeout, whether the restore succeeded or errored
                                finalize(() => {
                                    this.restoreSnapshotTimeout = null;
                                    subscriber.next();
                                    subscriber.complete();
                                })
                            )
                            .subscribe();
                    });
                })
                .subscribe();
        }).subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    clearSnapshot(id: string, event, selectCtrl) {
        console.debug(`${this.title} - clearSnapshot`);
        event.preventDefault();
        selectCtrl.close();

        const subscription = this.spatialSnapshotService.clearSnapshot(id).subscribe(id => this.eventBus.cast('spatialSnapshotCleared', id));

        this.eventBus.cast('nlr:busy', subscription);
    }

    refreshSnapshotIds() {
        this.snapshotIds = [...this.getSnapshotIds()];
    }

    getSnapshotIds() {
        return this.spatialSnapshotService.getSnapshotIds();
    }

    getSnapshotCount() {
        return this.getSnapshotIds().length;
    }

    getMaxSnapshotCount() {
        return this.spatialSnapshotService.maxSnapShotCount;
    }

    hasMaxSnapshotCount() {
        return this.getSnapshotCount() >= this.getMaxSnapshotCount();
    }

    getRemainingSnapshotCount() {
        return this.getMaxSnapshotCount() - this.getSnapshotCount();
    }
}
